import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import { productionOrdersData } from "types/api-types";

interface ProductionEntry {
  id?: number;
  productionOrderId: number | null;
  jobId: number | null;
  productId: number | null;
  logDate: Date;
  shift: "day" | "night" | "swing";
  supervisor: number | null;
  crewMembers: number[];

  // Production Metrics
  unitsPlanned: number;
  unitsProduced: number;
  unitsCompleted: number;
  unitsRejected: number;

  // Time Tracking
  shiftStartTime: string;
  shiftEndTime: string;
  breakTime: number; // minutes
  downtime: number; // minutes
  downtimeReason: string;

  // Materials Used
  materialsUsed: {
    materialId: number;
    quantityUsed: number;
    waste: number;
  }[];

  // Quality Metrics
  firstPassYield: number;
  defectRate: number;
  qualityNotes: string;

  // Equipment & Setup
  equipmentUsed: string[];
  moldSetups: number;
  toolChanges: number;
  maintenanceIssues: string;

  // Notes & Comments
  productionNotes: string;
  safetyIncidents: string;
  improvementSuggestions: string;

  // Weather Impact (for outdoor operations)
  weatherConditions: string;
  temperatureHigh: number | null;
  temperatureLow: number | null;
  humidity: number | null;
  weatherImpact: boolean;
}

class DailyProductionLogWizardStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  selectedDate: Date | null = new Date();
  selectedTable = "";
  productionItems: any[] = [];
  scanMode = false;
  scannedCode = "";

  // Production Entry Data
  entryData: ProductionEntry = {
    productionOrderId: null,
    jobId: null,
    productId: null,
    logDate: new Date(),
    shift: "day",
    supervisor: null,
    crewMembers: [],

    unitsPlanned: 0,
    unitsProduced: 0,
    unitsCompleted: 0,
    unitsRejected: 0,

    shiftStartTime: "07:00",
    shiftEndTime: "15:00",
    breakTime: 30,
    downtime: 0,
    downtimeReason: "",

    materialsUsed: [],

    firstPassYield: 100,
    defectRate: 0,
    qualityNotes: "",

    equipmentUsed: [],
    moldSetups: 0,
    toolChanges: 0,
    maintenanceIssues: "",

    productionNotes: "",
    safetyIncidents: "",
    improvementSuggestions: "",

    weatherConditions: "",
    temperatureHigh: null,
    temperatureLow: null,
    humidity: null,
    weatherImpact: false,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateEntryData = <K extends keyof ProductionEntry>(
    field: K,
    value: ProductionEntry[K]
  ) => {
    this.entryData[field] = value;
  };

  addCrewMember = (userId: number) => {
    if (!this.entryData.crewMembers.includes(userId)) {
      this.entryData.crewMembers = [...this.entryData.crewMembers, userId];
    }
  };

  removeCrewMember = (userId: number) => {
    this.entryData.crewMembers = this.entryData.crewMembers.filter(id => id !== userId);
  };

  addMaterialUsed = (materialId: number, quantityUsed: number, waste: number = 0) => {
    const existing = this.entryData.materialsUsed.find(m => m.materialId === materialId);
    if (existing) {
      existing.quantityUsed = quantityUsed;
      existing.waste = waste;
    } else {
      this.entryData.materialsUsed = [
        ...this.entryData.materialsUsed,
        { materialId, quantityUsed, waste }
      ];
    }
  };

  removeMaterialUsed = (materialId: number) => {
    this.entryData.materialsUsed = this.entryData.materialsUsed.filter(
      m => m.materialId !== materialId
    );
  };

  addEquipment = (equipment: string) => {
    if (!this.entryData.equipmentUsed.includes(equipment)) {
      this.entryData.equipmentUsed = [...this.entryData.equipmentUsed, equipment];
    }
  };

  removeEquipment = (equipment: string) => {
    this.entryData.equipmentUsed = this.entryData.equipmentUsed.filter(
      e => e !== equipment
    );
  };

  setSelectedDate = (date: Date | null) => {
    this.selectedDate = date;
    this.loadProductionData();
  };

  setSelectedTable = (table: string) => {
    this.selectedTable = table;
    this.loadProductionData();
  };

  setScanMode = (mode: boolean) => {
    this.scanMode = mode;
    if (!mode) {
      this.scannedCode = "";
    }
  };

  setScannedCode = (code: string) => {
    this.scannedCode = code;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Basic Information
        if (!this.entryData.productionOrderId) {
          this.setError("Please select a production order");
          return false;
        }
        if (!this.entryData.supervisor) {
          this.setError("Please select a supervisor");
          return false;
        }
        if (this.entryData.crewMembers.length === 0) {
          this.setError("Please add at least one crew member");
          return false;
        }
        break;

      case 1: // Production Metrics
        if (this.entryData.unitsProduced < 0) {
          this.setError("Units produced cannot be negative");
          return false;
        }
        if (this.entryData.unitsProduced > this.entryData.unitsPlanned * 1.5) {
          this.setError("Units produced seems unusually high compared to planned");
          return false;
        }
        break;

      case 2: // Time & Materials
        if (!this.entryData.shiftStartTime || !this.entryData.shiftEndTime) {
          this.setError("Please specify shift start and end times");
          return false;
        }
        if (this.entryData.downtime > 480) { // 8 hours
          this.setError("Downtime cannot exceed 8 hours");
          return false;
        }
        if (this.entryData.downtime > 0 && !this.entryData.downtimeReason) {
          this.setError("Please specify reason for downtime");
          return false;
        }
        break;

      case 3: // Quality & Equipment
        if (this.entryData.defectRate < 0 || this.entryData.defectRate > 100) {
          this.setError("Defect rate must be between 0 and 100%");
          return false;
        }
        if (this.entryData.firstPassYield < 0 || this.entryData.firstPassYield > 100) {
          this.setError("First pass yield must be between 0 and 100%");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const productionOrdersStore = this.rootStore.productionOrdersStore as ServiceStore;
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;
      const materialsStore = this.rootStore.materialsStore as ServiceStore;

      await Promise.all([
        productionOrdersStore?.find({
          query: {
            status: { $in: ["scheduled", "inProduction"] },
            $limit: 100,
            $sort: { plannedStartDate: 1 }
          }
        }),
        jobStore?.find({ query: { archived: false } }),
        userStore?.find(),
        materialsStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadProductionData = async () => {
    if (!this.selectedDate) return;

    this.loading = true;
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      const dateStr = this.selectedDate.toISOString().split("T")[0];

      // Filter panels for selected date and table
      const panels = (panelStore?.entities || []).filter((panel: any) => {
        const panelDate = panel.castingDate?.split("T")[0];
        return (
          panelDate === dateStr &&
          (!this.selectedTable || panel.tableNumber === this.selectedTable)
        );
      });

      runInAction(() => {
        this.productionItems = panels;
      });
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to load production data";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  handleStatusUpdate = async (panelId: number, newStatus: string) => {
    this.loading = true;
    this.error = null;

    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;

      // Update panel status
      await panelStore?.patch(panelId, {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString(),
        statusUpdatedBy: (userStore as any)?.currentUser?.id,
      });

      runInAction(() => {
        this.success = `Status updated to ${newStatus}`;
        // Update local list
        this.productionItems = this.productionItems.map((item) =>
          item.id === panelId ? { ...item, status: newStatus } : item
        );
      });

      setTimeout(() => {
        runInAction(() => {
          this.success = null;
        });
      }, 3000);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to update status";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  handleBulkStatusUpdate = async (status: string) => {
    this.loading = true;
    this.error = null;

    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;

      const updates = this.productionItems.map((item) =>
        panelStore?.patch(item.id, {
          status,
          statusUpdatedAt: new Date().toISOString(),
          statusUpdatedBy: (userStore as any)?.currentUser?.id,
        })
      );

      await Promise.all(updates);

      runInAction(() => {
        this.success = `Updated ${this.productionItems.length} items to ${status}`;
        this.productionItems = this.productionItems.map((item) => ({
          ...item,
          status,
        }));
      });

      setTimeout(() => {
        runInAction(() => {
          this.success = null;
        });
      }, 3000);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to update statuses";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  handleScanCode = async () => {
    if (!this.scannedCode) return;

    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;

      // Find panel by QR code or barcode
      const panel = panelStore?.entities.find(
        (p: any) => p.qrCode === this.scannedCode || p.barcode === this.scannedCode
      );

      if (!panel) {
        this.error = "Panel not found with this code";
        return;
      }

      // Add to current list if not already present
      if (!this.productionItems.find((item) => item.id === panel.id)) {
        runInAction(() => {
          this.productionItems = [...this.productionItems, panel];
          this.scannedCode = "";
          this.scanMode = false;
          this.success = "Panel added to list";
        });

        setTimeout(() => {
          runInAction(() => {
            this.success = null;
          });
        }, 3000);
      }
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to scan panel";
      });
    }
  };

  submitProductionLog = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create production log entry when backend service is available
      // For now, update the production order with actual metrics
      if (this.entryData.productionOrderId) {
        const productionOrdersStore = this.rootStore.productionOrdersStore as ServiceStore;

        await productionOrdersStore?.patch(this.entryData.productionOrderId, {
          actualHours: this.calculateShiftHours(),
          percentComplete: this.calculatePercentComplete(),
          status: this.entryData.unitsCompleted >= this.entryData.unitsPlanned ? "completed" : "inProduction",
        });
      }

      // Update material inventory if materials were used
      if (this.entryData.materialsUsed.length > 0) {
        const materialInventoryStore = this.rootStore.materialInventoryStore as ServiceStore;

        for (const material of this.entryData.materialsUsed) {
          // Deduct used materials from inventory
          const totalUsed = material.quantityUsed + material.waste;
          // TODO: Implement material consumption tracking
          console.log(`Would deduct ${totalUsed} units of material ${material.materialId}`);
        }
      }

      runInAction(() => {
        this.setSuccess("Production log submitted successfully!");
        this.activeStep = 5; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to submit production log");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.selectedDate = new Date();
    this.selectedTable = "";
    this.productionItems = [];
    this.scanMode = false;
    this.scannedCode = "";
    this.entryData = {
      productionOrderId: null,
      jobId: null,
      productId: null,
      logDate: new Date(),
      shift: "day",
      supervisor: null,
      crewMembers: [],

      unitsPlanned: 0,
      unitsProduced: 0,
      unitsCompleted: 0,
      unitsRejected: 0,

      shiftStartTime: "07:00",
      shiftEndTime: "15:00",
      breakTime: 30,
      downtime: 0,
      downtimeReason: "",

      materialsUsed: [],

      firstPassYield: 100,
      defectRate: 0,
      qualityNotes: "",

      equipmentUsed: [],
      moldSetups: 0,
      toolChanges: 0,
      maintenanceIssues: "",

      productionNotes: "",
      safetyIncidents: "",
      improvementSuggestions: "",

      weatherConditions: "",
      temperatureHigh: null,
      temperatureLow: null,
      humidity: null,
      weatherImpact: false,
    };
  };

  // Computed values
  get productionOrders() {
    const productionOrdersStore = this.rootStore.productionOrdersStore as ServiceStore;
    return productionOrdersStore?.entities || [];
  }

  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get materials() {
    const materialsStore = this.rootStore.materialsStore as ServiceStore;
    return materialsStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get tables() {
    const castingMapLayoutStore = this.rootStore.castingMapLayoutStore as ServiceStore;
    return castingMapLayoutStore?.entities || [];
  }

  get selectedProductionOrder() {
    if (!this.entryData.productionOrderId) return null;
    return this.productionOrders.find((po: any) => po.id === this.entryData.productionOrderId);
  }

  get selectedJob() {
    if (!this.entryData.jobId) return null;
    return this.jobs.find((j: any) => j.id === this.entryData.jobId);
  }

  get selectedProduct() {
    if (!this.entryData.productId) return null;
    return this.products.find((p: any) => p.id === this.entryData.productId);
  }

  get supervisorName() {
    if (!this.entryData.supervisor) return "";
    const supervisor = this.users.find((u: any) => u.id === this.entryData.supervisor);
    return supervisor?.name || "";
  }

  get crewMemberNames() {
    return this.entryData.crewMembers.map(id => {
      const user = this.users.find((u: any) => u.id === id);
      return user?.name || `User ${id}`;
    });
  }

  calculateShiftHours = (): number => {
    if (!this.entryData.shiftStartTime || !this.entryData.shiftEndTime) return 8;

    const start = new Date(`2000-01-01T${this.entryData.shiftStartTime}:00`);
    const end = new Date(`2000-01-01T${this.entryData.shiftEndTime}:00`);

    let hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (hours < 0) hours += 24; // Handle overnight shifts

    // Subtract break time and downtime
    hours -= (this.entryData.breakTime + this.entryData.downtime) / 60;

    return Math.max(0, hours);
  };

  calculatePercentComplete = (): number => {
    if (this.entryData.unitsPlanned === 0) return 0;
    return Math.round((this.entryData.unitsCompleted / this.entryData.unitsPlanned) * 100);
  };

  calculateEfficiency = (): number => {
    if (this.entryData.unitsPlanned === 0) return 0;
    return Math.round((this.entryData.unitsProduced / this.entryData.unitsPlanned) * 100);
  };

  calculateQualityRate = (): number => {
    if (this.entryData.unitsProduced === 0) return 100;
    const goodUnits = this.entryData.unitsProduced - this.entryData.unitsRejected;
    return Math.round((goodUnits / this.entryData.unitsProduced) * 100);
  };

  get productionEfficiencyColor() {
    const efficiency = this.calculateEfficiency();
    if (efficiency >= 95) return "success";
    if (efficiency >= 80) return "warning";
    return "error";
  }

  get qualityRateColor() {
    const quality = this.calculateQualityRate();
    if (quality >= 98) return "success";
    if (quality >= 95) return "warning";
    return "error";
  }

  getProductionStatusColor(status: string) {
    const statusColors: Record<string, string> = {
      MoldPrepped: "#2196f3",
      Poured: "#ff9800",
      Curing: "#9c27b0",
      Stripped: "#4caf50",
      Finished: "#00c853",
    };
    return statusColors[status] || "#666";
  }
}

export default DailyProductionLogWizardStore;